import { useQuery } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { useEffect, useMemo, useState } from 'react';

import { Btn } from '../../components/Btn';
import { Pill, type PillTone } from '../../components/Pill';
import { DataTable } from '../../components/Table';
import { listMovementsPage } from '../../data/fetchers';
import { fmtDate, fmtInt } from '../../lib/format';
import { useBranchScope } from '../../lib/useBranchScope';
import type { StockMovement } from '../../types/domain';
import { useStockCatalog } from './stockCatalog';
import { StockHeader } from './StockHeader';

const TYPE_TH: Record<StockMovement['type'], string> = {
  sale: 'ขาย',
  receive: 'รับเข้า',
  adjust: 'ปรับสต๊อก',
  'transfer-in': 'รับโอน',
  'transfer-out': 'โอนออก',
};
const TYPE_TONE: Record<StockMovement['type'], PillTone> = {
  sale: 'neutral',
  receive: 'ok',
  adjust: 'warn',
  'transfer-in': 'ok',
  'transfer-out': 'accent',
};

interface MvRow {
  id: string;
  date: string;
  type: StockMovement['type'];
  product: string;
  qty: number;
  reason: string;
}

const PAGE_SIZE = 25;

export function MovementLogView() {
  const { scope, ready } = useBranchScope();
  const [cursorHistory, setCursorHistory] = useState<string[]>([]);
  const scopeKey = `${scope.branchId ?? ''}|${scope.allowedBranchIds?.join(',') ?? ''}`;

  useEffect(() => {
    setCursorHistory([]);
  }, [scopeKey]);

  const before = cursorHistory.at(-1);
  const { data: page, isFetching } = useQuery({
    queryKey: ['movements', scope.branchId, scope.allowedBranchIds, before],
    queryFn: () => listMovementsPage({ scope, before, limit: PAGE_SIZE }),
    enabled: ready,
  });
  const movements = page?.movements ?? [];
  const catalog = useStockCatalog(movements.map((m) => m.productId));
  const products = catalog.products;
  const nameOf = (id: string) => products.find((p) => p.id === id)?.name ?? id;

  const rows = useMemo<MvRow[]>(
    () =>
      movements.map((m) => ({
        id: m.id,
        date: fmtDate(m.at),
        type: m.type,
        product: nameOf(m.productId),
        qty: m.qty,
        reason: m.reason ?? '',
      })),
    [movements, products],
  );

  const columns: ColumnDef<MvRow, unknown>[] = [
    { accessorKey: 'date', header: 'วันที่' },
    {
      accessorKey: 'type',
      header: 'ประเภท',
      cell: (c) => {
        const t = c.getValue<StockMovement['type']>();
        return <Pill tone={TYPE_TONE[t]}>{TYPE_TH[t]}</Pill>;
      },
    },
    { accessorKey: 'product', header: 'สินค้า' },
    {
      accessorKey: 'qty',
      header: 'จำนวน',
      cell: (c) => {
        const q = c.getValue<number>();
        return (
          <span className={`num ${q < 0 ? 'text-danger' : 'text-ok'}`}>
            {q > 0 ? '+' : ''}
            {fmtInt(q)}
          </span>
        );
      },
    },
    { accessorKey: 'reason', header: 'เหตุผล/อ้างอิง' },
  ];

  return (
    <div className="bo-fade">
      <StockHeader
        title="ประวัติการเคลื่อนไหว"
        subtitle="ตรวจสอบการขาย รับเข้า ปรับยอด และการโอนย้ายตามลำดับเวลา"
        status={{
          label: isFetching
            ? 'กำลังโหลดประวัติ'
            : `หน้า ${cursorHistory.length + 1} · ${movements.length} รายการ`,
          tone: isFetching ? 'neutral' : 'ok',
        }}
      />
      <DataTable
        columns={columns}
        data={rows}
        pageSize={PAGE_SIZE}
        mobileCards
      />
      <div className="mt-4 flex items-center justify-between gap-3">
        <Btn
          size="sm"
          disabled={cursorHistory.length === 0 || isFetching}
          onClick={() => setCursorHistory((history) => history.slice(0, -1))}
        >
          ก่อนหน้า
        </Btn>
        <span className="text-sm text-ink-3">
          หน้า {cursorHistory.length + 1}
        </span>
        <Btn
          size="sm"
          disabled={!page?.hasMore || !page.nextCursor || isFetching}
          onClick={() => {
            if (!page?.nextCursor) return;
            setCursorHistory((history) => [...history, page.nextCursor!]);
          }}
        >
          ถัดไป
        </Btn>
      </div>
    </div>
  );
}
